// League karmic system API client. Intent-only: the frontend never
// calculates karmic points — the backend owns all scoring logic.
const { VUE_APP_AI_BACKEND, VUE_APP_S3 } = process.env

const BASE = `${VUE_APP_AI_BACKEND || ''}`.trim().replace(/\/+$/, '') + '/vbapi/league'

const request = async (jwt, method, path, body = null) => {
  if (!jwt) throw new Error('NOT_AUTHENTICATED')
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${jwt}`,
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok || data.success === false) {
    throw new Error(data.error || `HTTP_${res.status}`)
  }
  return data
}

export const leagueApi = {
  // users
  me: (jwt) => request(jwt, 'GET', '/users/me'),
  users: (jwt) => request(jwt, 'GET', '/users'),
  userByHandle: (jwt, handle) => request(jwt, 'GET', `/users/by-handle/${encodeURIComponent(handle)}`),
  sectors: (jwt) => request(jwt, 'GET', '/users/sectors'),
  updateMySectors: (jwt, sectors) => request(jwt, 'PUT', '/users/me/sectors', { sectors }),
  followUser: (jwt, userId) => request(jwt, 'POST', `/users/${userId}/follow`),
  unfollowUser: (jwt, userId) => request(jwt, 'DELETE', `/users/${userId}/follow`),

  // posts
  feed: (jwt) => request(jwt, 'GET', '/posts'),
  createPost: (jwt, content, mediaUrl = null) =>
    request(jwt, 'POST', '/posts', { content, mediaUrl }),
  deletePost: (jwt, postId) => request(jwt, 'DELETE', `/posts/${postId}`),

  // reactions: type is like | love | dislike
  reactToPost: (jwt, postId, type) => request(jwt, 'POST', `/posts/${postId}/${type}`),
  unreactToPost: (jwt, postId, type) => request(jwt, 'DELETE', `/posts/${postId}/${type}`),

  // comments
  commentOnPost: (jwt, postId, content) =>
    request(jwt, 'POST', `/posts/${postId}/comments`, { content }),
  deleteComment: (jwt, commentId) => request(jwt, 'DELETE', `/comments/${commentId}`),

  // messages
  conversations: (jwt) => request(jwt, 'GET', '/messages'),
  conversation: (jwt, userId) => request(jwt, 'GET', `/messages/with/${userId}`),
  sendMessage: (jwt, toUserId, body) => request(jwt, 'POST', '/messages', { toUserId, body }),
  markMessageRead: (jwt, messageId) => request(jwt, 'PATCH', `/messages/${messageId}/read`),
}

// Resolves a profiles avatar/cover value (relative s3 path or full url)
// to a displayable url.
export const toLeagueMediaUrl = (rawValue) => {
  const raw = `${rawValue || ''}`.trim()
  if (!raw) return ''
  const base = `${VUE_APP_S3 || ''}`.trim().replace(/\/+$/, '')
  if (/^https?:\/\//i.test(raw)) {
    const m = raw.match(/\/s3\/(.+)$/i)
    if (m && m[1] && base) return `${base}/${m[1]}`
    return raw
  }
  if (raw.startsWith('/')) return raw
  if (!base) return raw
  return `${base}/${raw.replace(/^\/+/, '')}`
}

// Banner sources: cover first, avatar as fallback (banners derive from
// the avatar by default), then a neutral placeholder.
export const leagueBannerUrl = (user) =>
  toLeagueMediaUrl(user?.cover) ||
  toLeagueMediaUrl(user?.avatar) ||
  '/img/profile/baner.png'

export const leagueAvatarUrl = (user) =>
  toLeagueMediaUrl(user?.avatar) || '/img/profile/avatar.png'
